
import { UnitInputValue } from "@/types/unit";
import Vue from "vue";
import { Component, Inject, Prop } from "vue-property-decorator";
import MarginPaddingInputDragBar from "@/oxygen/classes/inputs/spacing/MarginPaddingInputDragBar.vue";
import { EasyEvent } from "@/util/easy-event";
import { OxygenControlsTreeContext } from "@/oxygen/classes/lib/types";
import { uiStore } from "@/store/store";
import {
  isValueInheritted,
  isValueOverwritten,
  shouldShowInheritanceAsPlaceholder
} from "@/oxygen/classes/lib/inheritance";

type SpacingType = "padding" | "margin";
type SpacingSide = "top" | "left" | "right" | "bottom";

type MarginPaddingInputValue = {
  margin?: {
    top?: UnitInputValue;
    left?: UnitInputValue;
    right?: UnitInputValue;
    bottom?: UnitInputValue;
  };
  padding?: {
    top?: UnitInputValue;
    left?: UnitInputValue;
    right?: UnitInputValue;
    bottom?: UnitInputValue;
  };
};

@Component({
  components: { MarginPaddingInputDragBar }
})
export default class OxygenSpacingInput extends Vue {
  @Inject({ from: "oxyContext" })
  readonly context!: OxygenControlsTreeContext;

  @Prop()
  value?: MarginPaddingInputValue;

  @Prop()
  placeholder?: MarginPaddingInputValue;

  @Prop()
  path!: string;

  get inheritance() {
    return this.context.getControlInheritanceByPrefix(this.path);
  }

  easyEvent = new EasyEvent();

  modifierKey: "shift" | "alt" | null = null;
  hoveredType: SpacingType | null = null;
  hoveredSide: SpacingSide | null = null;

  dragging = false;
  draggingType: SpacingType | null = null;

  sides: SpacingSide[] = ["top", "left", "right", "bottom"];

  updateProperty(
    type: SpacingType,
    side: SpacingSide,
    event: {
      value: UnitInputValue;
      modifierKey?: "shift" | "alt";
      transactionKey?: string;
    }
  ) {
    let newValue;
    const sideValue = event.value;

    if (type === "padding" && sideValue && Number(sideValue.number) < 0) {
      sideValue.number = 0;
      sideValue.style = `${sideValue.number}${sideValue.unit}`;
    }

    if (!event.modifierKey) {
      newValue = {
        ...this.value,
        [type]: {
          ...this.value?.[type],
          [side]: sideValue
        }
      };
    } else if (event.modifierKey === "alt") {
      const oppositeSide = this.getOppositeSide(side);
      newValue = {
        ...this.value,
        [type]: {
          ...this.value?.[type],
          [side]: sideValue,
          [oppositeSide]: sideValue
        }
      };
    } else if (event.modifierKey === "shift") {
      newValue = {
        ...this.value,
        [type]: {
          ...this.value?.[type],
          left: sideValue,
          right: sideValue,
          top: sideValue,
          bottom: sideValue
        }
      };
    }

    this.context.setControlValueAt(
      this.path,
      uiStore.currentBreakpoint.id,
      newValue,
      event?.transactionKey
    );
  }

  isSideActive(type: SpacingType, side: SpacingSide) {
    if (this.modifierKey === "shift" && this.hoveredType === type) {
      return true;
    }

    if (this.modifierKey === "alt" && this.hoveredType === type) {
      const isVerticalSide = (side: string | null) =>
        side === "top" || side === "bottom";
      const isHorizontalSide = (side: string | null) =>
        side === "left" || side === "right";

      if (isVerticalSide(this.hoveredSide)) {
        return isVerticalSide(side);
      }

      if (isHorizontalSide(this.hoveredSide)) {
        return isHorizontalSide(side);
      }
    }

    return false;
  }

  setHoveredType(type: SpacingType, side?: SpacingSide) {
    this.hoveredType = type;

    if (side) {
      this.hoveredSide = side;
    }
  }

  setDragging(dragging: boolean, type: SpacingType) {
    if (dragging) {
      this.dragging = true;
      this.draggingType = type;
    } else {
      this.dragging = false;
      this.draggingType = null;
    }
  }

  checkMetaKeys(event: KeyboardEvent | MouseEvent) {
    if (event.altKey) {
      this.modifierKey = "alt";
    } else if (event.shiftKey) {
      this.modifierKey = "shift";
    } else {
      this.modifierKey = null;
    }
  }

  onMouseEnter(event: MouseEvent) {
    this.checkMetaKeys(event);

    this.easyEvent.on("keyup", this.checkMetaKeys);
    this.easyEvent.on("keydown", this.checkMetaKeys);
  }

  onMouseLeave() {
    if (!this.dragging) {
      this.hoveredType = null;
      this.hoveredSide = null;

      this.easyEvent.off("keyup", this.checkMetaKeys);
      this.easyEvent.off("keydown", this.checkMetaKeys);
    }
  }

  beforeDestroy() {
    const container = this.$refs.container as HTMLElement;
    container?.removeEventListener("mouseenter", this.onMouseEnter);
    container?.removeEventListener("mouseleave", this.onMouseLeave);

    this.easyEvent.off("keyup", this.checkMetaKeys);
    this.easyEvent.off("keydown", this.checkMetaKeys);
  }

  getOppositeSide(side: SpacingSide) {
    if (side === "top") {
      return "bottom";
    } else if (side === "left") {
      return "right";
    } else if (side === "right") {
      return "left";
    } else {
      return "top";
    }
  }

  getPlaceholder(type: SpacingType, side: SpacingSide) {
    const key = `${type}.${side}`;

    if (!this.inheritance) return null;
    if (!this.inheritance[key]) return null;

    if (shouldShowInheritanceAsPlaceholder(this.inheritance[key])) {
      return this.inheritance[key]?.value;
    }

    return null;
  }

  isInheriting(type: SpacingType, side: SpacingSide) {
    const key = `${type}.${side}`;

    if (!this.inheritance) return false;
    if (!this.inheritance[key]) return false;

    return isValueInheritted(this.inheritance[key]);
  }

  isOverwritten(type: SpacingType, side: SpacingSide) {
    const key = `${type}.${side}`;

    if (!this.inheritance) return false;
    if (!this.inheritance[key]) return false;

    return isValueOverwritten(this.inheritance[key]);
  }
}
